/**
 * Personal tab of the ONE Profile drawer.
 *
 * Wires the shared presentational `ProfilePersonalSection` to ONE's
 * allauth-session user (the record `@aegix/auth-identity` re-fetches via the
 * boot-time session probe). ONE renders the identity header (initials avatar +
 * display name) above the details card.
 *
 * READ-ONLY on purpose: ONE has no general user-update endpoint wired (the
 * only PATCH it performs is the language sync inside the allauth adapter),
 * and a profile never diffs/saves against a login-time snapshot.
 * Rather than invent backend surface, the section renders with
 * `canEdit={false}` — no edit affordance — until ONE grows a fetched
 * user-detail + update seam.
 */
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  ProfilePersonalSection,
  type ProfilePersonalData,
} from '@tiaki/profile-ui';
import { useUser } from '@aegix/auth-identity';
import type { OneProfileUser } from './types';

export function ProfilePersonalTab() {
  const { t } = useTranslation();
  const user = useUser<OneProfileUser>();

  const displayName = user
    ? `${user.first_name} ${user.last_name}`.trim() || user.email
    : '';
  const initials = user
    ? `${(user.first_name || '').charAt(0)}${(user.last_name || '').charAt(0)}`.toUpperCase() ||
      user.email.charAt(0).toUpperCase()
    : '';

  const personalValue: ProfilePersonalData = {
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    phone: user?.cell_phone || '',
  };

  return (
    <VStack gap={5} align="stretch">
      {/* Identity header — preserved from the pre-tabbed drawer. */}
      <HStack gap={3}>
        <Box
          w={12}
          h={12}
          borderRadius="full"
          bg="button.primary"
          display="flex"
          alignItems="center"
          justifyContent="center"
          color="white"
          fontSize="lg"
          fontWeight="bold"
        >
          {initials}
        </Box>
        <Text fontSize="md" fontWeight="600" color="text.primary">
          {displayName}
        </Text>
      </HStack>

      <ProfilePersonalSection
        value={personalValue}
        // Unreachable while canEdit is false; the section requires the prop.
        onSave={() => undefined}
        canEdit={false}
        title={t('profile.details')}
        e2eTitle="profilePersonalSection"
      />
    </VStack>
  );
}
